import { useAuth0 } from '@auth0/auth0-react';
import { FC, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { Card, LoadingScreen } from '@growtherapy/sprout-ui';

import { useRequiredAgreements } from '@grow-therapy-team/seedling-components';

import { useEnterWaitingRoomCallback } from '../../nav/useEnterWaitingRoomCallback';
import { LoginSectionWrapper as LoginSection, LoginSectionProps } from '../auth/LoginSection';
import { useGetClientRequiresAuthedIntroRoom } from '../auth/useClientRequiresAuthedIntroRoom';
import {
  RequiredAgreementsWrapper as RequiredAgreements,
  RequiredAgreementsWrapperProps,
} from '../required-agreements/RequiredAgreements';
import { AVPermissionCallout } from './AVPermissionCallout';
import { InputControls, InputControlsProps } from './InputControls';
import {
  ProviderAvatarSectionWrapper as ProviderAvatarSection,
  ProviderAvatarSectionProps,
} from './ProviderAvatarSection';
import { ProviderLoginWrapper as ProviderLogin, ProviderLoginBaseProps } from './ProviderLogin';
import { useVisitorName } from './useVisitorName';

export interface MinimalPageContentProps extends InputControlsProps {
  className?: string;
  inputControlsComponent: FC<InputControlsProps>;
  providerLoginComponent: FC<ProviderLoginBaseProps>;
  providerAvatarSectionComponent: FC<ProviderAvatarSectionProps>;
  loginSectionComponent: FC<LoginSectionProps>;
  requiredAgreementsComponent: FC<RequiredAgreementsWrapperProps>;
  requiredAgreementsProps: RequiredAgreementsWrapperProps;
  showRequiredAgreements: boolean;
  loading: boolean;
}

export function MinimalPageContent({
  className,
  inputControlsComponent: InputControlsComponent,
  providerLoginComponent: ProviderLoginComponent,
  providerAvatarSectionComponent: ProviderAvatarComponent,
  loginSectionComponent: LoginSectionComponent,
  requiredAgreementsComponent: RequiredAgreementsComponent,
  requiredAgreementsProps,
  onEnterWaitingRoom,
  setVisitorName,
  visitorName,
  showRequiredAgreements,
  loading,
}: MinimalPageContentProps) {
  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <Card
      className={twMerge(
        'self-center sm:w-[34rem] z-10 sm:p-0 rebrand:sm:py-12 flex flex-col items-center w-full py-6 sm:py-12 gap-8 border-0 sm:border',
        className,
      )}
      data-testid='client-intro-state--control'
    >
      <ProviderAvatarComponent className='px-6 sm:px-12' />
      <LoginSectionComponent className='px-6 sm:px-12' />
      {showRequiredAgreements && <RequiredAgreementsComponent {...requiredAgreementsProps} className='mx-6 sm:mx-12' />}
      <InputControlsComponent
        className='flex flex-col items-center gap-8 px-6 sm:px-12'
        isRequired={true}
        onEnterWaitingRoom={onEnterWaitingRoom}
        setVisitorName={setVisitorName}
        showWaitingRoomButton={true}
        visitorName={visitorName}
      >
        <AVPermissionCallout />
      </InputControlsComponent>
      <ProviderLoginComponent className='px-6 sm:px-12' />
    </Card>
  );
}

export type MinimalPageContentWrapperProps = Pick<MinimalPageContentProps, 'className'>;

export function MinimalPageContentWrapper({ className }: MinimalPageContentWrapperProps) {
  const { data, loading: clientRequiresAuthedIntroRoomLoading } = useGetClientRequiresAuthedIntroRoom();
  const clientUserShortId = data?.clientRequiresAuthedIntroRoom?.clientUserShortId;
  const isMinor = data?.clientRequiresAuthedIntroRoom?.isMinor ?? false;

  const { isAuthenticated } = useAuth0();

  const {
    missingConsentTypes,
    clientUserWithParentGuardianInformation,
    isSubmitting,
    isFamilyAccount,
    requiresSelfSignedConsents,
    setFieldValue,
    formFields,
    submitConsents,
    loading,
  } = useRequiredAgreements(clientUserShortId ?? '', isAuthenticated && !!clientUserShortId);

  const hasPendingRequiredAgreements = missingConsentTypes && missingConsentTypes.length > 0;

  const { visitorName, visitorNameInput, visitorNameIsRequired, setVisitorName } = useVisitorName();
  const enterWaitingRoom = useEnterWaitingRoomCallback();

  const onEnterWaitingRoom = useCallback(() => {
    if (hasPendingRequiredAgreements) {
      const onSuccessfulSubmission = () => enterWaitingRoom(visitorName);
      submitConsents(onSuccessfulSubmission);
    } else {
      enterWaitingRoom(visitorName);
    }
  }, [hasPendingRequiredAgreements, enterWaitingRoom, submitConsents, visitorName]);

  return (
    <MinimalPageContent
      className={className}
      inputControlsComponent={InputControls}
      providerLoginComponent={ProviderLogin}
      providerAvatarSectionComponent={ProviderAvatarSection}
      loginSectionComponent={LoginSection}
      requiredAgreementsComponent={RequiredAgreements}
      requiredAgreementsProps={{
        missingConsentTypes,
        clientUserWithParentGuardianInformation,
        isSubmitting,
        isFamilyAccount,
        requiresSelfSignedConsents,
        setFieldValue,
        formFields,
      }}
      onEnterWaitingRoom={onEnterWaitingRoom}
      isRequired={visitorNameIsRequired}
      setVisitorName={setVisitorName}
      visitorName={visitorNameInput}
      showRequiredAgreements={isAuthenticated && hasPendingRequiredAgreements && !isMinor}
      loading={loading || clientRequiresAuthedIntroRoomLoading}
    />
  );
}
